import type {
    IWasteReturnListSourceDefinition,
    IWasteReturnListSourceMapping,
    IWasteReturnTemplateMapping,
} from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { WasteReturnRegFieldKeys } from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import {
    EA_WASTE_RETURN_LIST_SOURCE_DEFINITIONS,
    type IEaWasteReturnListSourceDefinition,
} from "./eaWasteReturnListSourceDictionary";
import { normalizeListSourceValueRange, buildGeographyAdjacentColumnMap } from "./mappingRangeAddress";
import { resolvePrimaryValueField } from "./listSourcePrimaryValueField";
import {
    mapFlatRowsToMapping,
    type IEaWasteReturnFieldRow,
} from "./wasteReturnFieldMappingAdapter";

export interface IEaWasteReturnListSourceRow {
    rowKey: IEaWasteReturnListSourceDefinition["rowKey"];
    label: string;
    regFieldKey: string;
    sheet: string;
    address: string;
}

function getDefinition(rowKey: string): IEaWasteReturnListSourceDefinition {
    const definition = EA_WASTE_RETURN_LIST_SOURCE_DEFINITIONS.find((item) => item.rowKey === rowKey);
    if (!definition) {
        throw new Error(`Unknown list source row key: ${rowKey}`);
    }
    return definition;
}

function readEffectiveDefinition(
    mapping: IWasteReturnTemplateMapping,
    definition: IEaWasteReturnListSourceDefinition
): IWasteReturnListSourceDefinition | null {
    const listSource = mapping.listSources[definition.regFieldKey];
    return listSource?.manualOverride ?? listSource?.autoDetected ?? null;
}

function rowFromDefinition(
    definition: IEaWasteReturnListSourceDefinition,
    source: IWasteReturnListSourceDefinition | null
): IEaWasteReturnListSourceRow {
    return {
        rowKey: definition.rowKey,
        label: definition.label,
        regFieldKey: definition.regFieldKey,
        sheet: source?.sheet?.trim() || definition.defaultSheet,
        address: source?.valueRange?.trim() || definition.defaultAddress,
    };
}

export function createDefaultListSourceRows(): IEaWasteReturnListSourceRow[] {
    return EA_WASTE_RETURN_LIST_SOURCE_DEFINITIONS.map((definition) => rowFromDefinition(definition, null));
}

export function mapMappingToListSourceRows(mapping: IWasteReturnTemplateMapping): IEaWasteReturnListSourceRow[] {
    return EA_WASTE_RETURN_LIST_SOURCE_DEFINITIONS.map((definition) => {
        const effective = readEffectiveDefinition(mapping, definition);
        return rowFromDefinition(definition, effective);
    });
}

function isRowBlank(row: IEaWasteReturnListSourceRow): boolean {
    return !row.sheet.trim() && !row.address.trim();
}

/** Bare tokens like `TCM_List` are named ranges; cell/range addresses contain row numbers or `:`. */
function looksLikeNamedRangeReference(valueRange: string): boolean {
    const trimmed = valueRange.trim();
    if (!trimmed) return false;
    if (trimmed.includes(":") || trimmed.includes("!")) return false;
    return !/\d/.test(trimmed);
}

function buildAdjacentColumnMap(regFieldKey: string, valueRange: string): Record<string, string> {
    if (regFieldKey !== WasteReturnRegFieldKeys.Geography) {
        return {};
    }
    return buildGeographyAdjacentColumnMap(valueRange);
}

function buildManualOverride(
    row: IEaWasteReturnListSourceRow,
    definition: IEaWasteReturnListSourceDefinition
): IWasteReturnListSourceDefinition | null {
    if (isRowBlank(row)) return null;

    const sheet = row.sheet.trim();
    const addressInput = row.address.trim();
    if (!sheet) return null;

    if (looksLikeNamedRangeReference(addressInput)) {
        return {
            sourceType: "namedRange",
            sheet,
            valueRange: addressInput,
            primaryValueField: resolvePrimaryValueField(definition.regFieldKey),
            adjacentColumnMap: {},
        };
    }

    const normalized = normalizeListSourceValueRange(addressInput);
    if (!normalized.success) return null;

    return {
        sourceType: "directRange",
        sheet,
        valueRange: normalized.valueRange,
        primaryValueField: resolvePrimaryValueField(definition.regFieldKey),
        adjacentColumnMap: buildAdjacentColumnMap(definition.regFieldKey, normalized.valueRange),
    };
}

function applyManualOverrideToField(
    listSources: IWasteReturnTemplateMapping["listSources"],
    fieldKey: string,
    manualOverride: IWasteReturnListSourceDefinition | null
): void {
    const current = listSources[fieldKey] ?? { extractedValues: [] };
    if (manualOverride) {
        listSources[fieldKey] = { ...current, manualOverride };
        return;
    }

    const { manualOverride: _removed, ...rest } = current;
    listSources[fieldKey] = { ...rest, extractedValues: current.extractedValues ?? [] };
}

export function mapListSourceRowsToMapping(
    rows: IEaWasteReturnListSourceRow[],
    mapping: IWasteReturnTemplateMapping
): IWasteReturnTemplateMapping {
    const listSources = { ...mapping.listSources };
    const rowByKey = new Map(rows.map((row) => [row.rowKey, row]));

    for (const definition of EA_WASTE_RETURN_LIST_SOURCE_DEFINITIONS) {
        const row = rowByKey.get(definition.rowKey);
        if (!row) continue;

        const manualOverride = buildManualOverride(row, definition);
        applyManualOverrideToField(listSources, definition.regFieldKey, manualOverride);
    }

    return { ...mapping, listSources };
}

export function buildMappingWithListSources(
    fieldRows: IEaWasteReturnFieldRow[],
    listSourceRows: IEaWasteReturnListSourceRow[],
    baseMapping?: IWasteReturnTemplateMapping
): IWasteReturnTemplateMapping {
    const withFields = mapFlatRowsToMapping(fieldRows, baseMapping);
    return mapListSourceRowsToMapping(listSourceRows, withFields);
}

export function listSourceRowsEqual(left: IEaWasteReturnListSourceRow[], right: IEaWasteReturnListSourceRow[]): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

export function getListSourceMappingKeys(row: IEaWasteReturnListSourceRow): string[] {
    const definition = getDefinition(row.rowKey);
    return [definition.regFieldKey];
}

/** @internal test helper */
export function __testBuildManualOverride(
    row: IEaWasteReturnListSourceRow,
    definition: IEaWasteReturnListSourceDefinition
): IWasteReturnListSourceDefinition | null {
    return buildManualOverride(row, definition);
}
